import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  CalendarToday as CalIcon,
  Close as CloseIcon,
  Assessment as ReportIcon,
  Comment as CommentIcon,
  Done as DoneIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  FileDownload as CsvIcon,
  TableChart as XlsIcon,
  Print as PrintIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ordersApi } from '@/api/orders';
import { tenantApi } from '@/api/tenant';
import { OutstandingInvoicesDialog } from '@/components/OutstandingInvoicesDialog';
import { useAuthStore } from '@/stores/authStore';
import { useSubscriptionGate } from '@/hooks/useSubscriptionGate';
import { PaginationFooter, ViewButton } from '@/components';
import type { Order, OrderStatus } from '@/types/order';
import type { PeerTenant } from '@/types/tenant';
import { downloadExcelFromBackend } from '@/utils/excelExport';

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

type DateFilter = 'CUSTOM' | 'TODAY' | 'YESTERDAY' | 'THIS_WEEK' | 'THIS_MONTH' | 'ALL';
type PartyTypeFilter = 'RETAILER' | 'DISTRIBUTOR' | 'SALESREP';

interface StatusOption {
  value: OrderStatus;
  label: string;
  color: string;
  bg: string;
}

const STATUS_OPTIONS: StatusOption[] = [
  { value: 'PLACED',              label: 'New',                 color: '#fff', bg: '#f97316' },
  { value: 'VIEWED',              label: 'Viewed',              color: '#fff', bg: '#6366f1' },
  { value: 'CONFIRMED',           label: 'Confirmed',           color: '#fff', bg: '#3b82f6' },
  { value: 'PARTIALLY_FULFILLED', label: 'Partially Fulfilled', color: '#fff', bg: '#f59e0b' },
  { value: 'INVOICED',            label: 'Billed',              color: '#fff', bg: '#06b6d4' },
  { value: 'DISPATCHED',          label: 'Transferred',         color: '#fff', bg: '#8b5cf6' },
  { value: 'REJECTED',            label: 'Rejected',            color: '#fff', bg: '#ef4444' },
  { value: 'CANCELLED',           label: 'Cancelled',           color: '#fff', bg: '#B22222' },
  { value: 'DELIVERED',           label: 'Completed',           color: '#fff', bg: '#22c55e' },
];

// Only these statuses appear in the filter sidebar
const FILTER_STATUS_OPTIONS = STATUS_OPTIONS.filter((s) =>
  !['PARTIALLY_FULFILLED', 'INVOICED', 'DISPATCHED'].includes(s.value)
);

// ---------------------------------------------------------------------------
// Report definitions
// ---------------------------------------------------------------------------

interface ReportDef { id: string; name: string; description: string }

const REPORT_DEFS: ReportDef[] = [
  { id: 'order-status',     name: 'Order Status Summary',    description: 'All received orders grouped and totalled by status' },
  { id: 'party-summary',    name: 'Party-wise Summary',      description: 'Orders count and revenue per retailer / distributor' },
  { id: 'daily-orders',     name: 'Daily Orders Report',     description: 'Orders, items and revenue grouped by date' },
  { id: 'rep-summary',      name: 'Rep Performance Summary', description: 'Orders, revenue and status breakdown per sales rep' },
  { id: 'revenue-analysis', name: 'Rep Revenue Analysis',    description: 'Revenue, average order value and share % per rep' },
  { id: 'order-details',    name: 'Rep Order Details',       description: 'All individual orders placed by each sales rep' },
];

function statusLabel(status: OrderStatus): string {
  return STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status;
}

function statusBg(status: OrderStatus): string {
  return STATUS_OPTIONS.find((s) => s.value === status)?.bg ?? '#9ca3af';
}

function partyTypeColor(value: PartyTypeFilter): string {
  if (value === 'RETAILER') return '#C17BAF';
  if (value === 'DISTRIBUTOR') return '#7B61FF';
  return '#0ea5e9';
}

const DATE_FILTER_COLOR = '#7c3aed';

/** YYYY-MM-DD from the browser's LOCAL calendar date — `toISOString()` converts to UTC first,
 *  which silently shifts "today" by a day near local midnight (e.g. IST is UTC+5:30). */
function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Converts the Time Period quick-filter into a start/end date pair (YYYY-MM-DD) sent to the backend. */
function computeQuickDateRange(filter: DateFilter): { startDate?: string; endDate?: string } {
  const todayStr = toLocalDateStr(new Date());
  switch (filter) {
    case 'TODAY':
      return { startDate: todayStr, endDate: todayStr };
    case 'YESTERDAY': {
      const y = new Date(); y.setDate(y.getDate() - 1);
      const s = toLocalDateStr(y);
      return { startDate: s, endDate: s };
    }
    case 'THIS_WEEK': {
      const now = new Date();
      const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay());
      return { startDate: toLocalDateStr(weekStart), endDate: todayStr };
    }
    case 'THIS_MONTH': {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: toLocalDateStr(monthStart), endDate: todayStr };
    }
    default:
      return {};
  }
}

function formatDateGroupHeader(dateStr: string) {
  const d = new Date(dateStr);
  const datePart = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const dayPart = isToday ? 'Today' : isYesterday ? 'Yesterday' : d.toLocaleDateString('en-GB', { weekday: 'long' });
  return <>{datePart} <Box component="span" sx={{ color: '#008080' }}>[{dayPart}]</Box></>;
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatAmount(n: number): string {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const TENANT_TYPE_PALETTE: Record<string, { bg: string; fg: string }> = {
  RETAILER:    { bg: '#C17BAF', fg: '#FFFFFF' },
  WHOLESALER:  { bg: '#7B61FF', fg: '#FFFFFF' },
  DISTRIBUTOR: { bg: '#3DD68C', fg: '#0E3520' },
  OTHERS:      { bg: '#9CA3AF', fg: '#1F2937' },
};

// ---------------------------------------------------------------------------
// Download / print helpers
// ---------------------------------------------------------------------------

const CSV_HEADERS = 'order_id,interface_id,customer_id,customer_code,customer_name,product_id,product_code,product,pack,quantity,free_qty,scheme,mrp,rate,total';

function buildCsvRows(order: Order, retailerName: string): string {
  return order.items.map((item) =>
    [
      order.id,
      order.orderNumber ?? '',
      order.retailerTenantId,
      '',
      `"${retailerName.replace(/"/g, '""')}"`,
      item.productId,
      item.sku,
      `"${item.productName.replace(/"/g, '""')}"`,
      '',
      item.qty,
      item.freeQty ?? 0,
      item.scheme || '',
      item.mrp ?? '',
      item.unitPrice,
      item.lineTotal,
    ].join(',')
  ).join('\n');
}

function downloadCsv(order: Order, retailerName: string) {
  const content = `${CSV_HEADERS}\n${buildCsvRows(order, retailerName)}`;
  triggerDownload(new Blob([content], { type: 'text/csv' }), `order_${order.orderNumber ?? order.id}.csv`);
}

function downloadXls(order: Order, retailerName: string) {
  downloadExcelFromBackend({
    title: `Order ${order.orderNumber ?? order.id}`,
    subTitle: `Retailer: ${retailerName} | Date: ${new Date().toLocaleDateString('en-IN')}`,
    headers: ['Order ID', 'Order #', 'Customer ID', 'Customer Code', 'Customer Name', 'Product ID', 'SKU', 'Product', 'Pack', 'Qty', 'Free Qty', 'Scheme', 'MRP', 'Rate', 'Total'],
    rows: order.items.map(item => [
      order.id, order.orderNumber ?? '', order.retailerTenantId, '', retailerName,
      item.productId, item.sku, item.productName, '', item.qty, item.freeQty ?? 0,
      item.scheme || '', item.mrp ?? '', item.unitPrice, item.lineTotal,
    ]),
  });
}

// ─── Filter persistence (session-only — survives nav away/back, clears on browser close) ──
const FILTERS_SESSION_PREFIX = 'orderFilters:';
function loadSessionFilters<T>(key: string): Partial<T> {
  try {
    const raw = sessionStorage.getItem(FILTERS_SESSION_PREFIX + key);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function saveSessionFilters<T extends object>(key: string, data: T) {
  try { sessionStorage.setItem(FILTERS_SESSION_PREFIX + key, JSON.stringify(data)); } catch {}
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function printOrder(order: Order, retailerName: string) {
  const rows = order.items.map((item) => `
    <tr>
      <td>${item.sku}</td>
      <td>${item.productName}</td>
      <td style="text-align:right">${item.qty}</td>
      <td style="text-align:right">${item.mrp ?? ''}</td>
      <td style="text-align:right">${item.unitPrice.toFixed(2)}</td>
      <td style="text-align:right">${item.lineTotal.toFixed(2)}</td>
    </tr>`).join('');
  const html = `<!DOCTYPE html><html><head><title>Order #${order.orderNumber ?? order.id}</title>
<style>body{font-family:sans-serif;font-size:12px;margin:20px}h3{margin-bottom:4px}p{margin:2px 0 10px}
table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:5px 8px}
th{background:#f0f0f0;font-weight:600}.total{font-weight:700}</style></head>
<body><h3>Order #${order.orderNumber ?? order.id}</h3>
<p>Party: ${retailerName} &nbsp;|&nbsp; Date: ${new Date(order.orderDate ?? order.createdAt).toLocaleDateString('en-GB')}</p>
<table><thead><tr><th>SKU</th><th>Product</th><th>Qty</th><th>MRP</th><th>Rate</th><th>Total</th></tr></thead>
<tbody>${rows}</tbody>
<tfoot><tr><td colspan="5" style="text-align:right" class="total">Grand Total</td>
<td style="text-align:right" class="total">₹${Number(order.totalAmount).toFixed(2)}</td></tr></tfoot>
</table></body></html>`;
  const w = window.open('', '_blank');
  if (w) { w.document.write(html); w.document.close(); w.focus(); w.print(); }
}

// ---------------------------------------------------------------------------
// Reject dialog
// ---------------------------------------------------------------------------

function RejectDialog({ open, onClose, onConfirm, loading }: {
  open: boolean; onClose: () => void; onConfirm: (reason: string) => void; loading: boolean;
}) {
  const [reason, setReason] = useState('');
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth onClick={(e) => e.stopPropagation()}>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Reason for Rejection</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth multiline rows={3} size="small"
          placeholder="e.g. No stock available" value={reason}
          onChange={(e) => setReason(e.target.value)} sx={{ mt: 0.5 }} />
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button size="small" onClick={onClose}>Cancel</Button>
        <Button size="small" variant="contained" color="error"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason.trim())} sx={{ fontWeight: 700 }}>
          Confirm Rejection
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Row action menu
// ---------------------------------------------------------------------------

function OrderRowMenu({
  order, retailerName, onAccept, onReject, onComplete, onUploadInvoice, actionLoading,
}: {
  order: Order;
  retailerName: string;
  onAccept: () => void;
  onReject: (reason: string) => void;
  onComplete: () => void;
  onUploadInvoice: (file: File) => void;
  actionLoading: boolean;
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const close = () => setAnchor(null);
  const canAccept = order.status === 'PLACED' || order.status === 'VIEWED';
  const canReject = order.status === 'PLACED' || order.status === 'VIEWED' || order.status === 'CONFIRMED';
  const canComplete = order.status === 'CONFIRMED' || order.status === 'PARTIALLY_FULFILLED' || order.status === 'DISPATCHED';

  const menuItem = (icon: React.ReactNode, label: string, color: string, onClick: () => void, disabled = false) => (
    <MenuItem dense disabled={disabled || actionLoading} onClick={() => { close(); onClick(); }}
      sx={{ fontSize: 13, color, gap: 1, py: 0.6 }}>
      {icon}{label}
    </MenuItem>
  );

  return (
    <>
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.txt,.csv" hidden
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadInvoice(f); e.target.value = ''; }} />
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        sx={{ color: '#9ca3af', '&:hover': { color: '#374151' } }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 200 } }}>

        {menuItem(<CsvIcon sx={{ fontSize: 16 }} />, 'Download CSV', '#374151', () => downloadCsv(order, retailerName))}
        {menuItem(<XlsIcon sx={{ fontSize: 16 }} />, 'Download Excel', '#374151', () => downloadXls(order, retailerName))}
        {menuItem(<PrintIcon sx={{ fontSize: 16 }} />, 'Print Order', '#374151', () => printOrder(order, retailerName))}

        <Divider sx={{ my: 0.5 }} />

        {canAccept && menuItem(<CheckCircleIcon sx={{ fontSize: 16 }} />, 'Confirm', '#16a34a', onAccept)}
        {canReject && menuItem(<CancelIcon sx={{ fontSize: 16 }} />, 'Reject', '#dc2626', () => setRejectOpen(true))}
        {canComplete && menuItem(<DoneIcon sx={{ fontSize: 16 }} />, 'Complete Order', '#3b82f6', onComplete)}

        {order.status === 'DELIVERED' && <Divider sx={{ my: 0.5 }} />}

        {order.status === 'DELIVERED' && menuItem(
          <UploadIcon sx={{ fontSize: 16 }} />,
          order.invoiceFileName ? 'Re-upload Invoice' : 'Upload Invoice',
          '#374151',
          () => fileRef.current?.click())}

        {!canAccept && !canReject && !canComplete && order.status !== 'DELIVERED' && (
          <MenuItem dense disabled sx={{ fontSize: 13 }}>No actions available</MenuItem>
        )}
      </Menu>
      <RejectDialog open={rejectOpen} onClose={() => setRejectOpen(false)}
        onConfirm={(reason) => { setRejectOpen(false); onReject(reason); }}
        loading={actionLoading} />
    </>
  );
}

// ---------------------------------------------------------------------------
// OrderInboxPage
// ---------------------------------------------------------------------------

export function OrderInboxPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sidebarOpen,      setSidebarOpen]      = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const savedInboxFilters = loadSessionFilters<{
    selectedStatuses: OrderStatus[]; selectedPartyTypes: PartyTypeFilter[]; selectedRepIds: number[];
    dateFilter: DateFilter; searchMode: 'product' | 'orderId' | 'party' | 'phone'; searchInput: string;
    appliedSearch: { mode: 'product' | 'orderId' | 'party' | 'phone'; query: string } | null;
    startDate: string; endDate: string;
  }>('orderInbox');
  const [selectedStatuses, setSelectedStatuses] = useState<OrderStatus[]>(() => {
    const s = searchParams.get('status');
    if (s) return [s as OrderStatus];
    return savedInboxFilters.selectedStatuses ?? [];
  });
  const [selectedPartyTypes, setSelectedPartyTypes] = useState<PartyTypeFilter[]>(() => savedInboxFilters.selectedPartyTypes ?? []);
  const [selectedRepIds, setSelectedRepIds] = useState<number[]>(() => savedInboxFilters.selectedRepIds ?? []);
  const [dateFilter, setDateFilter] = useState<DateFilter>(() => savedInboxFilters.dateFilter ?? 'ALL');
  const [searchMode,    setSearchMode]    = useState<'product' | 'orderId' | 'party' | 'phone'>(() => savedInboxFilters.searchMode ?? 'party');
  const [searchInput,   setSearchInput]   = useState(() => savedInboxFilters.searchInput ?? '');
  const [appliedSearch, setAppliedSearch] = useState<{ mode: 'product' | 'orderId' | 'party' | 'phone'; query: string } | null>(() => savedInboxFilters.appliedSearch ?? null);
  const [startDate, setStartDate] = useState(() => savedInboxFilters.startDate ?? '');
  const [endDate,   setEndDate]   = useState(() => savedInboxFilters.endDate ?? '');
  const dateRangeError = startDate && endDate && startDate > endDate ? 'Start date must be on or before end date' : '';
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [retailers, setRetailers] = useState<Map<number, PeerTenant>>(new Map());
  const [repsMap, setRepsMap] = useState<Map<number, string>>(new Map());
  const [viewParty, setViewParty] = useState<PeerTenant | null>(null);
  const [viewOutstandingParty, setViewOutstandingParty] = useState<PeerTenant | null>(null);
  /** Toggle between the order list and the rep-wise reports panel. */
  const [repReportView, setRepReportView] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string>(REPORT_DEFS[0].id);
  const [generateAnchor, setGenerateAnchor] = useState<null | HTMLElement>(null);
  const [pageNum, setPageNum] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<50 | 100>(50);
  const [shortageStore, setShortageStore] = useState<Map<number, { orderId: number; shortages: { sku: string; productName: string; orderedQty: number; receivedQty: number; shortageQty: number }[] }>>(new Map());

  const fetchOrders = useCallback(async (filters: {
    partyName?: string; partyPhone?: string; orderNumber?: string; productQuery?: string; startDate?: string; endDate?: string;
    statuses?: OrderStatus[]; partyTypes?: PartyTypeFilter[]; repIds?: number[];
  } = {}) => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const result = await ordersApi.list({ distributorId: user.tenantId, size: 500, ...filters });
      const content = result.content ?? [];
      setOrders(content);
      if (content.length) {
        ordersApi.getShortagesForOrders(content.map(o => o.id))
          .then(rows => {
            const map = new Map<number, { orderId: number; shortages: typeof rows }>();
            for (const row of rows) {
              const rec = map.get(row.orderId) ?? { orderId: row.orderId, shortages: [] };
              rec.shortages.push(row);
              map.set(row.orderId, rec);
            }
            setShortageStore(map);
          })
          .catch(() => {});
      }
    } catch {
      setError('Failed to load orders. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  type ManualOverrides = { partyName?: string; partyPhone?: string; orderNumber?: string; productQuery?: string; startDate?: string; endDate?: string };
  type AppliedFilters = ManualOverrides & {
    statuses?: OrderStatus[]; partyTypes?: PartyTypeFilter[]; repIds?: number[];
  };
  /** Filters actually sent to the backend last time — kept only for Refresh to re-apply them. */
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});
  /**
   * The search / date-range values from the LAST EXPLICIT search action (search icon / Enter),
   * kept separate from `appliedFilters` on purpose: `appliedFilters.startDate/endDate` may hold a
   * Time Period quick-preset's computed dates, which must NOT leak back in as a manual value once
   * the preset is switched back to "All Time" — that bug previously caused every filter combo to
   * silently keep an invisible stale date range and return zero results.
   */
  const manualFiltersRef = useRef<ManualOverrides>({
    partyName: savedInboxFilters.appliedSearch?.mode === 'party' ? savedInboxFilters.appliedSearch.query : undefined,
    partyPhone: savedInboxFilters.appliedSearch?.mode === 'phone' ? savedInboxFilters.appliedSearch.query : undefined,
    orderNumber: savedInboxFilters.appliedSearch?.mode === 'orderId' ? savedInboxFilters.appliedSearch.query : undefined,
    productQuery: savedInboxFilters.appliedSearch?.mode === 'product' ? savedInboxFilters.appliedSearch.query : undefined,
    startDate: savedInboxFilters.startDate || undefined,
    endDate: savedInboxFilters.endDate || undefined,
  });

  /**
   * Builds the full backend filter set from current sidebar selections plus the supplied
   * search / date-range overrides (falls back to the last manually-searched values for those,
   * since they only change on an explicit search action, never live).
   */
  function buildFilters(manualOverrides: ManualOverrides = {}): AppliedFilters {
    const quick = dateFilter !== 'ALL' ? computeQuickDateRange(dateFilter) : {};
    const sd = 'startDate' in manualOverrides ? manualOverrides.startDate : manualFiltersRef.current.startDate;
    const ed = 'endDate' in manualOverrides ? manualOverrides.endDate : manualFiltersRef.current.endDate;
    const pn = 'partyName' in manualOverrides ? manualOverrides.partyName : manualFiltersRef.current.partyName;
    const pp = 'partyPhone' in manualOverrides ? manualOverrides.partyPhone : manualFiltersRef.current.partyPhone;
    const on = 'orderNumber' in manualOverrides ? manualOverrides.orderNumber : manualFiltersRef.current.orderNumber;
    const pq = 'productQuery' in manualOverrides ? manualOverrides.productQuery : manualFiltersRef.current.productQuery;
    if (Object.keys(manualOverrides).length > 0) {
      manualFiltersRef.current = { partyName: pn, partyPhone: pp, orderNumber: on, productQuery: pq, startDate: sd, endDate: ed };
    }
    return {
      partyName: pn,
      partyPhone: pp,
      orderNumber: on,
      productQuery: pq,
      startDate: quick.startDate ?? (sd || undefined),
      endDate: quick.endDate ?? (ed || undefined),
      statuses: selectedStatuses.length ? selectedStatuses : undefined,
      partyTypes: selectedPartyTypes.length ? selectedPartyTypes : undefined,
      repIds: selectedRepIds.length ? selectedRepIds : undefined,
    };
  }

  function applyAndFetch(manualOverrides: ManualOverrides = {}) {
    const next = buildFilters(manualOverrides);
    setAppliedFilters(next);
    void fetchOrders(next);
  }

  /** Runs the backend search — only invoked on an explicit user action (search icon / Enter), never on every keystroke. */
  function runSearch() {
    if (dateRangeError) return;
    const trimmed = searchInput.trim();
    setAppliedSearch(trimmed ? { mode: searchMode, query: trimmed } : null);
    applyAndFetch({
      partyName: searchMode === 'party' && trimmed ? trimmed : undefined,
      partyPhone: searchMode === 'phone' && trimmed ? trimmed : undefined,
      orderNumber: searchMode === 'orderId' && trimmed ? trimmed : undefined,
      productQuery: searchMode === 'product' && trimmed ? trimmed : undefined,
      startDate, endDate,
    });
  }

  function clearSearch() {
    setSearchInput('');
    setAppliedSearch(null);
    applyAndFetch({ partyName: undefined, partyPhone: undefined, orderNumber: undefined, productQuery: undefined });
  }

  function clearDateRange() {
    setStartDate(''); setEndDate('');
    applyAndFetch({ startDate: '', endDate: '' });
  }

  useEffect(() => {
    tenantApi.listPeers(undefined, 0, 500)
      .then((result) => {
        const map = new Map<number, PeerTenant>();
        for (const peer of result.content) map.set(peer.id, peer);
        setRetailers(map);
      })
      .catch(() => {});
    tenantApi.listReps(undefined, 0, 500)
      .then((result) => {
        const map = new Map<number, string>();
        for (const rep of result.content) map.set(rep.id, rep.name);
        setRepsMap(map);
      })
      .catch(() => {});
  }, []);

  // Status / Network Type / Time Period are applied to the backend immediately on change —
  // never filtered against an already-fetched page. This also performs the initial load
  // (e.g. respecting a ?status= deep link). selectedRepIds has no dedicated sidebar control
  // anymore, but stays wired here so the Rep Reports drill-down click still filters correctly.
  useEffect(() => {
    applyAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatuses, selectedPartyTypes, selectedRepIds, dateFilter, fetchOrders]);

  // Persist filter selections in sessionStorage so navigating away and back restores them.
  useEffect(() => {
    saveSessionFilters('orderInbox', {
      selectedStatuses, selectedPartyTypes, selectedRepIds, dateFilter,
      searchMode, searchInput, appliedSearch, startDate, endDate,
    });
  }, [selectedStatuses, selectedPartyTypes, selectedRepIds, dateFilter, searchMode, searchInput, appliedSearch, startDate, endDate]);

  // ── Subscription gate ──────────────────────────────────────────────────────
  const subGate = useSubscriptionGate();
  const [subOverdueOpen, setSubOverdueOpen] = useState(false);
  const [subDueOpen, setSubDueOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);
  // Auto-open overdue dialog when blocked
  useEffect(() => {
    if (!subGate.loading && subGate.isBlocked) setSubOverdueOpen(true);
  }, [subGate.loading, subGate.isBlocked]);

  function toggleStatus(status: OrderStatus) {
    setSelectedStatuses((prev) => prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]);
  }

  function togglePartyType(t: PartyTypeFilter) {
    setSelectedPartyTypes((prev) => prev.includes(t) ? prev.filter((p) => p !== t) : [...prev, t]);
  }

  // Status, Network Type, Time Period, Party Name, Order ID, Product Name and the Start/End date
  // range are all applied server-side (see the filters effect and runSearch) — never re-filtered
  // here against an already-fetched page.
  const filteredOrders = useMemo(() => {
    return orders
      .slice()
      .sort((a, b) => {
        const ta = new Date(a.orderDate ?? a.createdAt).getTime();
        const tb = new Date(b.orderDate ?? b.createdAt).getTime();
        return tb - ta;
      });
  }, [orders]);

  /** Per-rep aggregated stats — used in the Rep Reports panel. */
  const repStats = useMemo(() => {
    type RepStat = { repName: string; totalOrders: number; totalRevenue: number; pending: number; confirmed: number; delivered: number; cancelled: number };
    const map = new Map<number, RepStat>();
    for (const o of orders) {
      if (o.salesRepTenantId == null) continue;
      const key = o.salesRepTenantId;
      const ex: RepStat = map.get(key) ?? {
        repName: o.salesRepName ?? repsMap.get(key) ?? `Rep #${key}`,
        totalOrders: 0, totalRevenue: 0, pending: 0, confirmed: 0, delivered: 0, cancelled: 0,
      };
      ex.totalOrders++;
      ex.totalRevenue += Number(o.totalAmount) || 0;
      if (o.status === 'PLACED' || o.status === 'VIEWED') ex.pending++;
      else if (['CONFIRMED', 'PARTIALLY_FULFILLED', 'INVOICED', 'DISPATCHED'].includes(o.status)) ex.confirmed++;
      else if (o.status === 'DELIVERED') ex.delivered++;
      else if (o.status === 'CANCELLED' || o.status === 'REJECTED') ex.cancelled++;
      map.set(key, ex);
    }
    return Array.from(map.entries())
      .map(([id, v]) => ({ repId: id, ...v }))
      .sort((a, b) => b.totalRevenue - a.totalRevenue);
  }, [orders, repsMap]);

  /** All orders grouped by status — used in Order Status Summary report. */
  const orderStatusStats = useMemo(() => {
    const map = new Map<string, { label: string; count: number; revenue: number }>();
    for (const o of orders) {
      const key = o.status;
      const ex = map.get(key) ?? { label: statusLabel(o.status), count: 0, revenue: 0 };
      ex.count++;
      ex.revenue += Number(o.totalAmount) || 0;
      map.set(key, ex);
    }
    return Array.from(map.entries())
      .map(([status, v]) => ({ status, ...v }))
      .sort((a, b) => b.count - a.count);
  }, [orders]);

  /** Order count per status, shown next to each Status filter checkbox. */
  const statusCounts = useMemo(() => {
    const map = new Map<OrderStatus, number>();
    for (const o of orders) map.set(o.status, (map.get(o.status) ?? 0) + 1);
    return map;
  }, [orders]);

  /** Order count per Network Type, shown next to each checkbox. Mirrors the backend classification
   *  in OrderSpecifications: retailer tenant type, wholesaler/distributor tenant type, or has a sales rep. */
  const partyTypeCounts = useMemo(() => {
    const map = new Map<PartyTypeFilter, number>();
    for (const o of orders) {
      const tenantType = retailers.get(o.retailerTenantId)?.tenantType;
      if (tenantType === 'RETAILER') map.set('RETAILER', (map.get('RETAILER') ?? 0) + 1);
      if (tenantType === 'WHOLESALER') map.set('DISTRIBUTOR', (map.get('DISTRIBUTOR') ?? 0) + 1);
      if (o.salesRepTenantId != null) map.set('SALESREP', (map.get('SALESREP') ?? 0) + 1);
    }
    return map;
  }, [orders, retailers]);

  /** Order count per Time Period quick-filter, shown next to each radio option. */
  const dateFilterCounts = useMemo(() => {
    const map = new Map<DateFilter, number>();
    map.set('ALL', orders.length);
    for (const v of ['TODAY', 'YESTERDAY', 'THIS_WEEK', 'THIS_MONTH'] as DateFilter[]) {
      const { startDate, endDate } = computeQuickDateRange(v);
      map.set(v, orders.filter((o) => {
        const d = toLocalDateStr(new Date(o.orderDate ?? o.createdAt));
        return (!startDate || d >= startDate) && (!endDate || d <= endDate);
      }).length);
    }
    return map;
  }, [orders]);

  /** Orders grouped by retailer — used in Party-wise Summary report. */
  const partyStats = useMemo(() => {
    const map = new Map<number, { name: string; count: number; revenue: number; lastDate: string }>();
    for (const o of orders) {
      const key = o.retailerTenantId;
      const name = retailers.get(key)?.name ?? `Party #${key}`;
      const date = (o.orderDate ?? o.createdAt ?? '').slice(0, 10);
      const ex = map.get(key) ?? { name, count: 0, revenue: 0, lastDate: date };
      ex.count++;
      ex.revenue += Number(o.totalAmount) || 0;
      if (date > ex.lastDate) ex.lastDate = date;
      map.set(key, ex);
    }
    return Array.from(map.entries())
      .map(([id, v]) => ({ partyId: id, ...v }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [orders, retailers]);

  /** Orders grouped by date — used in Daily Orders report. */
  const dailyStats = useMemo(() => {
    const map = new Map<string, { count: number; items: number; revenue: number }>();
    for (const o of orders) {
      const key = (o.orderDate ?? o.createdAt ?? '').slice(0, 10);
      const ex = map.get(key) ?? { count: 0, items: 0, revenue: 0 };
      ex.count++;
      ex.items += o.items?.reduce((s, i) => s + (i.qty || 0), 0) ?? 0;
      ex.revenue += Number(o.totalAmount) || 0;
      map.set(key, ex);
    }
    return Array.from(map.entries())
      .map(([date, v]) => ({ date, ...v }))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [orders]);

  // --- subscription gate helpers ---

  const DUE_REMINDER_KEY = 'b2b_sub_due_last_shown';
  function shouldShowDueReminder() {
    return Date.now() - Number(localStorage.getItem(DUE_REMINDER_KEY) ?? 0) > 3_600_000;
  }
  function markDueReminderShown() {
    localStorage.setItem(DUE_REMINDER_KEY, String(Date.now()));
  }
  function withSubGate(action: () => void): void {
    if (subGate.isBlocked) { setSubOverdueOpen(true); return; }
    if (!subGate.loading && subGate.hasDue && shouldShowDueReminder()) {
      markDueReminderShown();
      pendingActionRef.current = action;
      setSubDueOpen(true);
      return;
    }
    action();
  }

  // --- action handlers ---

  async function coreAccept(order: Order) {
    setActionLoading(true); setActionError('');
    try {
      const updated = await ordersApi.confirm(order.id);
      setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    } catch { setActionError('Failed to accept order.'); }
    finally { setActionLoading(false); }
  }
  function handleAccept(order: Order) { withSubGate(() => void coreAccept(order)); }

  async function coreReject(order: Order, reason: string) {
    setActionLoading(true); setActionError('');
    try {
      const updated = await ordersApi.reject(order.id, reason);
      setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    } catch { setActionError('Failed to reject order.'); }
    finally { setActionLoading(false); }
  }
  function handleReject(order: Order, reason: string) { withSubGate(() => void coreReject(order, reason)); }

  async function coreComplete(order: Order) {
    setActionLoading(true); setActionError('');
    try {
      const updated = await ordersApi.complete(order.id);
      setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    } catch { setActionError('Failed to complete order. Please try again.'); }
    finally { setActionLoading(false); }
  }
  function handleComplete(order: Order) { withSubGate(() => void coreComplete(order)); }

  async function coreUploadInvoice(order: Order, file: File) {
    setActionLoading(true); setActionError('');
    try {
      const updated = await ordersApi.uploadInvoice(order.id, file);
      setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
      setActionSuccess('Invoice uploaded successfully.');
    } catch (err: unknown) {
      setActionError((err as { message?: string })?.message || 'Failed to upload invoice.');
    }
    finally { setActionLoading(false); }
  }
  function handleUploadInvoice(order: Order, file: File) { withSubGate(() => void coreUploadInvoice(order, file)); }

  async function coreViewAndNavigate(order: Order) {
    if (order.status === 'PLACED') {
      // Optimistic: badge changes immediately in the list
      setOrders((prev) =>
        prev.map((o) => (o.id === order.id ? { ...o, status: 'VIEWED' as OrderStatus } : o))
      );
      // Await so the server is updated before fetchOrders runs on remount
      try {
        const updated = await ordersApi.view(order.id);
        setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
      } catch {
        // Keep optimistic update; detail page will retry
      }
    }
    navigate(`/orders/inbox/${order.id}`);
  }
  function handleViewAndNavigate(order: Order) { withSubGate(() => void coreViewAndNavigate(order)); }

  // Group by date
  useEffect(() => { setPageNum(0); }, [rowsPerPage, filteredOrders.length]);

  const pagedOrders = useMemo(() =>
    filteredOrders.slice(pageNum * rowsPerPage, (pageNum + 1) * rowsPerPage),
  [filteredOrders, pageNum, rowsPerPage]);

  const grouped = useMemo(() => {
    const map = new Map<string, Order[]>();
    for (const o of pagedOrders) {
      // ISO date key (YYYY-MM-DD) so lexicographic sort is also chronological
      const key = new Date(o.orderDate ?? o.createdAt).toISOString().slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(o);
    }
    // pagedOrders is already sorted newest-first, so groups appear in insertion order;
    // sort by key descending to guarantee the correct group order regardless.
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [pagedOrders]);

  function printSelectedOrders() {
    const toPrint = filteredOrders.filter(o => selectedIds.has(o.id));
    if (toPrint.length === 0) return;
    const pages = toPrint.map(order => {
      const retailerName = retailers.get(order.retailerTenantId)?.name ?? `Party #${order.retailerTenantId}`;
      const rows = order.items.map(item => `
        <tr>
          <td>${item.sku}</td>
          <td>${item.productName}</td>
          <td style="text-align:right">${item.qty}</td>
          <td style="text-align:right">${item.mrp ?? ''}</td>
          <td style="text-align:right">${item.unitPrice.toFixed(2)}</td>
          <td style="text-align:right">${item.lineTotal.toFixed(2)}</td>
        </tr>`).join('');
      return `<div style="page-break-after:always">
        <h3 style="margin-bottom:4px">Order #${order.orderNumber ?? order.id}</h3>
        <p style="margin:2px 0 10px">Party: ${retailerName} &nbsp;|&nbsp; Date: ${new Date(order.orderDate ?? order.createdAt).toLocaleDateString('en-GB')}</p>
        <table style="width:100%;border-collapse:collapse">
          <thead><tr><th>SKU</th><th>Product</th><th>Qty</th><th>MRP</th><th>Rate</th><th>Total</th></tr></thead>
          <tbody>${rows}</tbody>
          <tfoot><tr><td colspan="5" style="text-align:right;font-weight:700">Grand Total</td>
          <td style="text-align:right;font-weight:700">₹${Number(order.totalAmount).toFixed(2)}</td></tr></tfoot>
        </table></div>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><title>Orders Print</title>
<style>body{font-family:sans-serif;font-size:12px;margin:20px}h3{margin-bottom:4px}p{margin:2px 0 10px}
table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:5px 8px}
th{background:#f0f0f0;font-weight:600}</style></head><body>${pages}</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); w.print(); }
  }

  // ---------------------------------------------------------------------------
  // Report download helpers
  // ---------------------------------------------------------------------------

  function downloadReportExcel() {
    const def = REPORT_DEFS.find(r => r.id === selectedReportId)!;
    let headers: string[];
    let rows: (string | number)[][];

    if (selectedReportId === 'order-status') {
      headers = ['Status', 'Orders', 'Revenue (₹)', 'Revenue Share (%)'];
      const totalRev = orderStatusStats.reduce((a, s) => a + s.revenue, 0);
      rows = orderStatusStats.map(s => [
        s.label, s.count, s.revenue.toFixed(2),
        totalRev > 0 ? ((s.revenue / totalRev) * 100).toFixed(1) + '%' : '0%',
      ]);
      rows.push(['TOTAL', orderStatusStats.reduce((a,s)=>a+s.count,0), orderStatusStats.reduce((a,s)=>a+s.revenue,0).toFixed(2), '100%']);
    } else if (selectedReportId === 'party-summary') {
      headers = ['Party / Retailer', 'Orders', 'Revenue (₹)', 'Last Order Date'];
      rows = partyStats.map(p => [p.name, p.count, p.revenue.toFixed(2), p.lastDate]);
      rows.push(['TOTAL', partyStats.reduce((a,p)=>a+p.count,0), partyStats.reduce((a,p)=>a+p.revenue,0).toFixed(2), '']);
    } else if (selectedReportId === 'daily-orders') {
      headers = ['Date', 'Orders', 'Total Items', 'Revenue (₹)'];
      rows = dailyStats.map(d => [d.date, d.count, d.items, d.revenue.toFixed(2)]);
      rows.push(['TOTAL', dailyStats.reduce((a,d)=>a+d.count,0), dailyStats.reduce((a,d)=>a+d.items,0), dailyStats.reduce((a,d)=>a+d.revenue,0).toFixed(2)]);
    } else if (selectedReportId === 'revenue-analysis') {
      const totalRev = repStats.reduce((a, r) => a + r.totalRevenue, 0);
      headers = ['Sales Rep', 'Orders', 'Revenue (₹)', 'Avg Order Value (₹)', 'Revenue Share (%)'];
      rows = repStats.map(s => [
        s.repName, s.totalOrders,
        s.totalRevenue.toFixed(2),
        (s.totalOrders > 0 ? s.totalRevenue / s.totalOrders : 0).toFixed(2),
        totalRev > 0 ? ((s.totalRevenue / totalRev) * 100).toFixed(1) + '%' : '0%',
      ]);
    } else if (selectedReportId === 'order-details') {
      headers = ['Sales Rep', 'Order #', 'Party', 'Date', 'Amount (₹)', 'Status'];
      rows = orders
        .filter(o => o.salesRepTenantId != null)
        .sort((a, b) => (a.salesRepName ?? '').localeCompare(b.salesRepName ?? ''))
        .map(o => [
          o.salesRepName ?? `Rep #${o.salesRepTenantId}`,
          o.orderNumber ?? String(o.id),
          retailers.get(o.retailerTenantId)?.name ?? `Party #${o.retailerTenantId}`,
          new Date(o.orderDate ?? o.createdAt).toLocaleDateString('en-GB'),
          Number(o.totalAmount).toFixed(2),
          statusLabel(o.status),
        ]);
    } else {
      // rep-summary (default)
      headers = ['Sales Rep', 'Orders', 'Revenue (₹)', 'Pending', 'Confirmed', 'Completed', 'Cancelled/Rejected'];
      rows = repStats.map(s => [s.repName, s.totalOrders, s.totalRevenue.toFixed(2), s.pending, s.confirmed, s.delivered, s.cancelled]);
      rows.push(['TOTAL',
        repStats.reduce((a, r) => a + r.totalOrders, 0),
        repStats.reduce((a, r) => a + r.totalRevenue, 0).toFixed(2),
        repStats.reduce((a, r) => a + r.pending, 0),
        repStats.reduce((a, r) => a + r.confirmed, 0),
        repStats.reduce((a, r) => a + r.delivered, 0),
        repStats.reduce((a, r) => a + r.cancelled, 0),
      ]);
    }

    const content = [headers, ...rows].map(r => r.join('\t')).join('\n');
    triggerDownload(new Blob(['﻿' + content], { type: 'application/vnd.ms-excel' }), `${def.id}_report.xls`);
  }

  function downloadReportPdf() {
    const def = REPORT_DEFS.find(r => r.id === selectedReportId)!;
    const style = `body{font-family:sans-serif;font-size:12px;margin:24px}h3{margin:0 0 2px}p{margin:0 0 14px;color:#6b7280;font-size:11px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #e5e7eb;padding:5px 9px}th{background:#f3f4f6;font-weight:700;font-size:10px;text-transform:uppercase;letter-spacing:.4px}tr:nth-child(even){background:#fafafa}.total-row{background:#f3f4f6;font-weight:700}`;
    let tableHtml = '';

    if (selectedReportId === 'order-status') {
      const totalRev = orderStatusStats.reduce((a,s)=>a+s.revenue,0);
      const totalCnt = orderStatusStats.reduce((a,s)=>a+s.count,0);
      tableHtml = `<table><thead><tr>${['Status','Orders','Revenue (₹)','Revenue Share (%)'].map(h=>`<th style="text-align:right">${h}</th>`).join('')}</tr></thead><tbody>${
        orderStatusStats.map(s=>`<tr><td style="text-align:left"><b>${s.label}</b></td><td style="text-align:right">${s.count}</td><td style="text-align:right">₹${formatAmount(s.revenue)}</td><td style="text-align:right">${totalRev>0?((s.revenue/totalRev)*100).toFixed(1):0}%</td></tr>`).join('')
      }</tbody><tfoot><tr class="total-row"><td>TOTAL</td><td style="text-align:right">${totalCnt}</td><td style="text-align:right">₹${formatAmount(totalRev)}</td><td style="text-align:right">100%</td></tr></tfoot></table>`;
    } else if (selectedReportId === 'party-summary') {
      const totalRev = partyStats.reduce((a,p)=>a+p.revenue,0);
      const totalCnt = partyStats.reduce((a,p)=>a+p.count,0);
      tableHtml = `<table><thead><tr>${['Party / Retailer','Orders','Revenue (₹)','Last Order'].map(h=>`<th>${h}</th>`).join('')}</tr></thead><tbody>${
        partyStats.map(p=>`<tr><td><b>${p.name}</b></td><td style="text-align:right">${p.count}</td><td style="text-align:right">₹${formatAmount(p.revenue)}</td><td>${p.lastDate}</td></tr>`).join('')
      }</tbody><tfoot><tr class="total-row"><td>TOTAL — ${partyStats.length} parties</td><td style="text-align:right">${totalCnt}</td><td style="text-align:right">₹${formatAmount(totalRev)}</td><td></td></tr></tfoot></table>`;
    } else if (selectedReportId === 'daily-orders') {
      const totalCnt = dailyStats.reduce((a,d)=>a+d.count,0);
      const totalItems = dailyStats.reduce((a,d)=>a+d.items,0);
      const totalRev = dailyStats.reduce((a,d)=>a+d.revenue,0);
      tableHtml = `<table><thead><tr>${['Date','Orders','Total Items','Revenue (₹)'].map(h=>`<th>${h}</th>`).join('')}</tr></thead><tbody>${
        dailyStats.map(d=>`<tr><td><b>${d.date}</b></td><td style="text-align:right">${d.count}</td><td style="text-align:right">${d.items}</td><td style="text-align:right">₹${formatAmount(d.revenue)}</td></tr>`).join('')
      }</tbody><tfoot><tr class="total-row"><td>TOTAL</td><td style="text-align:right">${totalCnt}</td><td style="text-align:right">${totalItems}</td><td style="text-align:right">₹${formatAmount(totalRev)}</td></tr></tfoot></table>`;
    } else if (selectedReportId === 'revenue-analysis') {
      const totalRev = repStats.reduce((a, r) => a + r.totalRevenue, 0);
      tableHtml = `<table><thead><tr>${['Sales Rep','Orders','Revenue (₹)','Avg Order Value (₹)','Revenue Share (%)'].map(h=>`<th>${h}</th>`).join('')}</tr></thead><tbody>${
        repStats.map(s=>`<tr><td><b>👤 ${s.repName}</b></td><td style="text-align:right">${s.totalOrders}</td><td style="text-align:right">₹${formatAmount(s.totalRevenue)}</td><td style="text-align:right">₹${formatAmount(s.totalOrders>0?s.totalRevenue/s.totalOrders:0)}</td><td style="text-align:right">${totalRev>0?((s.totalRevenue/totalRev)*100).toFixed(1):0}%</td></tr>`).join('')
      }</tbody></table>`;
    } else if (selectedReportId === 'order-details') {
      const repOrders = orders.filter(o=>o.salesRepTenantId!=null).sort((a,b)=>(a.salesRepName??'').localeCompare(b.salesRepName??''));
      tableHtml = `<table><thead><tr>${['Sales Rep','Order #','Party','Date','Amount (₹)','Status'].map(h=>`<th>${h}</th>`).join('')}</tr></thead><tbody>${
        repOrders.map((o)=>`<tr><td><b>👤 ${o.salesRepName??`Rep #${o.salesRepTenantId}`}</b></td><td>${o.orderNumber??o.id}</td><td>${retailers.get(o.retailerTenantId)?.name??`Party #${o.retailerTenantId}`}</td><td>${new Date(o.orderDate??o.createdAt).toLocaleDateString('en-GB')}</td><td style="text-align:right">₹${formatAmount(Number(o.totalAmount))}</td><td>${statusLabel(o.status)}</td></tr>`).join('')
      }</tbody></table>`;
    } else {
      // rep-summary
      const tot = { orders: repStats.reduce((a,r)=>a+r.totalOrders,0), rev: repStats.reduce((a,r)=>a+r.totalRevenue,0), p: repStats.reduce((a,r)=>a+r.pending,0), c: repStats.reduce((a,r)=>a+r.confirmed,0), d: repStats.reduce((a,r)=>a+r.delivered,0), x: repStats.reduce((a,r)=>a+r.cancelled,0) };
      tableHtml = `<table><thead><tr>${['Sales Rep','Orders','Revenue (₹)','Pending','Confirmed','Completed','Cancelled/Rejected'].map(h=>`<th style="text-align:right">${h}</th>`).join('')}</tr></thead><tbody>${
        repStats.map(s=>`<tr><td><b>👤 ${s.repName}</b></td><td style="text-align:right">${s.totalOrders}</td><td style="text-align:right">₹${formatAmount(s.totalRevenue)}</td><td style="text-align:right;color:#f97316">${s.pending||'—'}</td><td style="text-align:right;color:#3b82f6">${s.confirmed||'—'}</td><td style="text-align:right;color:#16a34a">${s.delivered||'—'}</td><td style="text-align:right;color:#ef4444">${s.cancelled||'—'}</td></tr>`).join('')
      }</tbody><tfoot><tr class="total-row"><td>TOTAL</td><td style="text-align:right">${tot.orders}</td><td style="text-align:right">₹${formatAmount(tot.rev)}</td><td style="text-align:right">${tot.p||'—'}</td><td style="text-align:right">${tot.c||'—'}</td><td style="text-align:right">${tot.d||'—'}</td><td style="text-align:right">${tot.x||'—'}</td></tr></tfoot></table>`;
    }

    const html = `<!DOCTYPE html><html><head><title>${def.name}</title><style>${style}</style></head><body><h3>${def.name}</h3><p>${def.description} &nbsp;|&nbsp; Generated: ${new Date().toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'})}</p>${tableHtml}</body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.focus(); w.print(); }
  }

  const clearAll = () => {
    setSelectedStatuses([]); setSelectedPartyTypes([]); setSelectedRepIds([]);
    setDateFilter('ALL'); setSearchInput(''); setAppliedSearch(null);
    setStartDate(''); setEndDate('');
    manualFiltersRef.current = {};
    setAppliedFilters({});
    void fetchOrders({});
  };

  // grid template shared between header and rows — single-line layout:
  // chevron | buyer (badge+name+rep tag) | phone | location | order# | value | spacer | view | status | menu | checkbox
  // Every data column has a 100px floor; the name column's floor is 150px (icon-only utility
  // columns — chevron, menu, checkbox — stay small since they hold a single glyph, not data).
  const COLS = '24px minmax(150px,300px) minmax(100px,110px) minmax(100px,130px) minmax(100px,170px) minmax(16px,1fr) 100px 100px 28px 28px';
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  function toggleExpanded(id: number) {
    setExpandedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // ── Subscription overdue — block the whole page ───────────────────────────
  if (subGate.isBlocked) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 2, p: 4 }}>
        <Typography sx={{ fontSize: 40 }}>🔒</Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#b45309' }}>Subscription Overdue</Typography>
        <Typography sx={{ fontSize: 14, color: '#6b7280', textAlign: 'center', maxWidth: 440 }}>
          Your subscription invoice is overdue by <strong>{subGate.daysSince} day{subGate.daysSince !== 1 ? 's' : ''}</strong>.
          Received orders are blocked.
        </Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#dc2626' }}>
          Amount Due: ₹{subGate.dueAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
        <Typography sx={{ fontSize: 13, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 1, px: 2, py: 1, maxWidth: 440, textAlign: 'center', color: '#991b1b' }}>
          Please contact <strong>Ardinotech Ltd</strong> for support or make the payment to restore access.
        </Typography>
        {orders.length > 0 && (() => {
          const incomplete = orders.filter(o => !['DELIVERED','CANCELLED','REJECTED'].includes(o.status));
          if (incomplete.length === 0) return null;
          return (
            <Box sx={{ display: 'flex', gap: 3, mt: 1, px: 3, py: 1.5, border: '1px solid #e5e7eb', borderRadius: 1, bgcolor: '#fff' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#152346' }}>{incomplete.length}</Typography>
                <Typography sx={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>PENDING ORDERS</Typography>
              </Box>
              <Box sx={{ width: '1px', bgcolor: '#e5e7eb' }} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#152346' }}>
                  ₹{formatAmount(incomplete.reduce((s, o) => s + (Number(o.totalAmount) || 0), 0))}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>PENDING WORTH</Typography>
              </Box>
            </Box>
          );
        })()}
        <Button variant="contained" sx={{ bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' } }}
          onClick={() => navigate('/my-subscription')}>
          Pay Now
        </Button>
        {/* Overdue dialog — auto-opens */}
        <Dialog open={subOverdueOpen} onClose={() => setSubOverdueOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: '#dc2626' }}>🔒 Subscription Overdue</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 13, mb: 1.5 }}>
              Your subscription invoice is overdue by <strong>{subGate.daysSince} day{subGate.daysSince !== 1 ? 's' : ''}</strong>.
              You cannot view or process received orders.
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#dc2626', mb: 1 }}>
              Due: ₹{subGate.dueAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Typography sx={{ fontSize: 13, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 1, px: 1.5, py: 1, color: '#991b1b' }}>
              Please contact <strong>Ardinotech Ltd</strong> for support or make the payment immediately.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
            <Button size="small" onClick={() => setSubOverdueOpen(false)}>Close</Button>
            <Button size="small" variant="contained"
              sx={{ bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' } }}
              onClick={() => { setSubOverdueOpen(false); navigate('/my-subscription'); }}>
              Pay Now
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 72px)', overflow: 'hidden' }}>
      {/* ── Left sidebar (desktop only, collapsible) ── */}
      <Box sx={{ width: sidebarCollapsed ? 0 : 196, flexShrink: 0, overflow: 'hidden', transition: 'width 0.2s ease', display: { xs: 'none', md: 'block' } }}>
        <Box sx={{ width: 196, height: '100%', display: 'flex', flexDirection: 'column', borderRight: '1px solid #e5e7eb', bgcolor: '#fafbfc', overflowY: 'auto', fontSize: 13 }}>
          <Box sx={{ px: 1.75, py: 1, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f3f4f6' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151', letterSpacing: 0.6 }}>Filters</Typography>
            <Box component="button" onClick={clearAll}
              sx={{ border: '1px solid #ef4444', bgcolor: 'transparent', color: '#ef4444', fontSize: 10, fontWeight: 700, px: 0.75, py: 0.2, borderRadius: 0.5, cursor: 'pointer', letterSpacing: 0.3, '&:hover': { bgcolor: '#fef2f2' } }}>
              × CLEAR
            </Box>
          </Box>

          <SidebarSection title="Network Type" onClear={selectedPartyTypes.length > 0 ? () => setSelectedPartyTypes([]) : undefined}>
            {([
              { value: 'RETAILER',    label: 'Retailer Orders' },
              { value: 'DISTRIBUTOR', label: 'Supplier Orders' },
              { value: 'SALESREP',    label: 'Rep Orders' },
            ] as { value: PartyTypeFilter; label: string }[]).map(({ value, label }) => (
              <FormControlLabel key={value}
                control={<Checkbox size="small" checked={selectedPartyTypes.includes(value)} onChange={() => togglePartyType(value)} sx={{ p: 0.3 }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <Typography sx={{ fontSize: 12, color: '#374151' }}>{label}</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: partyTypeColor(value) }}>({partyTypeCounts.get(value) ?? 0})</Typography>
                  </Box>
                }
                sx={{ mx: 0, my: 0.1, display: 'flex', gap: 0.25, width: '100%' }} />
            ))}
          </SidebarSection>

          <SidebarSection title="Status" onClear={selectedStatuses.length > 0 ? () => setSelectedStatuses([]) : undefined}>
            {FILTER_STATUS_OPTIONS.map((opt) => {
              return (
                <FormControlLabel key={opt.value}
                  control={<Checkbox size="small" checked={selectedStatuses.includes(opt.value)} onChange={() => toggleStatus(opt.value)} sx={{ p: 0.3 }} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <Typography sx={{ fontSize: 12, color: '#374151' }}>{opt.label} Orders</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: statusBg(opt.value) }}>({statusCounts.get(opt.value) ?? 0})</Typography>
                    </Box>
                  }
                  sx={{ mx: 0, my: 0.1, display: 'flex', gap: 0.25, width: '100%' }} />
              );
            })}
          </SidebarSection>

          <SidebarSection title="Time Period" onClear={dateFilter !== 'ALL' ? () => setDateFilter('ALL') : undefined}>
            <RadioGroup value={dateFilter} onChange={(e) => setDateFilter(e.target.value as DateFilter)}>
              {(['ALL','TODAY','YESTERDAY','THIS_WEEK','THIS_MONTH'] as DateFilter[]).map((v) => (
                <FormControlLabel key={v} value={v}
                  control={<Radio size="small" sx={{ p: 0.3 }} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <Typography sx={{ fontSize: 12, color: '#374151' }}>{v === 'ALL' ? 'All Time' : v === 'TODAY' ? 'Current Day' : v === 'THIS_WEEK' ? 'Current Week' : v === 'THIS_MONTH' ? 'Current Month' : v.charAt(0) + v.slice(1).toLowerCase()}</Typography>
                      {/* Count hidden for "All Time" — keeping the code, just not rendering it.
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: DATE_FILTER_COLOR }}>({dateFilterCounts.get(v) ?? 0})</Typography>
                      */}
                      {v !== 'ALL' && (
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: DATE_FILTER_COLOR }}>({dateFilterCounts.get(v) ?? 0})</Typography>
                      )}
                    </Box>
                  }
                  sx={{ mx: 0, my: 0.1, width: '100%' }} />
              ))}
            </RadioGroup>
          </SidebarSection>

        </Box>
      </Box>

      {/* ── Main content ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* Top bar */}
        <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #e5e7eb', bgcolor: 'background.paper', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#152346', flexShrink: 0, mr: 0.5 }}>Order Inbox</Typography>

            {/* Search by select + input + icon button */}
            <Select
              size="small"
              value={searchMode}
              onChange={(e) => setSearchMode(e.target.value as 'product' | 'orderId' | 'party' | 'phone')}
              sx={{ fontSize: 12, minWidth: 130, '& .MuiOutlinedInput-notchedOutline': { borderRadius: '4px 0 0 4px' } }}
            >
              <MenuItem value="party"   sx={{ fontSize: 12 }}>Party Name</MenuItem>
              <MenuItem value="phone"   sx={{ fontSize: 12 }}>Phone Number</MenuItem>
              <MenuItem value="orderId" sx={{ fontSize: 12 }}>Order ID</MenuItem>
              <MenuItem value="product" sx={{ fontSize: 12 }}>Product Name</MenuItem>
            </Select>
            <TextField
              size="small"
              placeholder={searchMode === 'product' ? 'Search product…' : searchMode === 'orderId' ? 'Search order ID…' : searchMode === 'phone' ? 'Search phone number…' : 'Search party name…'}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
              sx={{ flex: '1 1 140px', maxWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: '0 4px 4px 0', fontSize: 13 } }}
            />
            <Tooltip title="Search">
              <IconButton
                size="small"
                onClick={runSearch}
                disabled={Boolean(dateRangeError)}
                sx={{ color: appliedSearch?.query.trim() ? '#7c3aed' : undefined, bgcolor: appliedSearch?.query.trim() ? '#ede9fe' : undefined, borderRadius: 1 }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {appliedSearch?.query.trim() && (
              <Tooltip title="Clear search">
                <IconButton size="small" onClick={clearSearch} sx={{ borderRadius: 1 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* Date range */}
            <CalIcon sx={{ color: '#9ca3af', fontSize: 18, ml: 0.5 }} />
            <TextField type="date" size="small" label="Start Date" value={startDate}
              error={Boolean(dateRangeError)}
              onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }}
              inputProps={{ max: endDate || undefined }}
              sx={{ width: 150, '& .MuiInputBase-input': { fontSize: 12 } }} />
            <TextField type="date" size="small" label="End Date" value={endDate}
              error={Boolean(dateRangeError)}
              onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate || undefined }}
              sx={{ width: 150, '& .MuiInputBase-input': { fontSize: 12 } }} />
            {(startDate || endDate) && !dateRangeError && (
              <Tooltip title="Clear date range">
                <IconButton size="small" onClick={clearDateRange} sx={{ borderRadius: 1 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Box sx={{ flex: 1 }} />

            <Tooltip title="Filters">
              <IconButton size="small" onClick={() => setSidebarOpen(true)}
                sx={{ display: { xs: 'inline-flex', md: 'none' }, color: (selectedStatuses.length + selectedPartyTypes.length + selectedRepIds.length) > 0 || dateFilter !== 'ALL' ? '#7c3aed' : undefined }}>
                <FilterListIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={sidebarCollapsed ? 'Show Filters' : 'Hide Filters'}>
              <IconButton size="small" onClick={() => setSidebarCollapsed(v => !v)}
                sx={{ display: { xs: 'none', md: 'inline-flex' }, color: sidebarCollapsed ? undefined : '#7c3aed', bgcolor: sidebarCollapsed ? undefined : '#ede9fe', borderRadius: 1 }}>
                <FilterListIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={repReportView ? 'Back to Orders' : 'Rep-wise Reports'}>
              <IconButton size="small" onClick={() => setRepReportView((v) => !v)}
                sx={{ color: repReportView ? '#7c3aed' : undefined, bgcolor: repReportView ? '#ede9fe' : undefined, borderRadius: 1 }}>
                <ReportIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={selectedIds.size > 0 ? `Print ${selectedIds.size} selected order(s)` : 'Select orders to print'}>
              <span>
                <IconButton size="small" onClick={printSelectedOrders} disabled={selectedIds.size === 0 || repReportView}>
                  <PrintIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => fetchOrders(appliedFilters)} disabled={loading}>
                {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mx: 2, mt: 1, fontSize: 12 }}>{error}</Alert>}
        {dateRangeError && <Alert severity="error" sx={{ mx: 2, mt: 1, fontSize: 12 }}>{dateRangeError}</Alert>}
        {actionError && <Alert severity="error" sx={{ mx: 2, mt: 1, fontSize: 12 }} onClose={() => setActionError('')}>{actionError}</Alert>}

        {loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}

        {/* ── Rep Reports Panel ── */}
        {!loading && repReportView && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Single header strip: title + stats | report selector + Generate — all on one line */}
            <Box sx={{ px: 2, py: 0.6, borderBottom: '1px solid #e5e7eb', bgcolor: '#f9fafb', display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, flexWrap: 'nowrap' }}>
              <ReportIcon sx={{ color: '#7c3aed', fontSize: 15, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#152346', whiteSpace: 'nowrap' }}>Reports</Typography>
              <Typography sx={{ fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                {orders.length} order{orders.length !== 1 ? 's' : ''} · {repStats.length} rep{repStats.length !== 1 ? 's' : ''}
              </Typography>

              <Box sx={{ flex: 1 }} />

              {/* Searchable report selector */}
              <Autocomplete
                size="small"
                options={REPORT_DEFS}
                getOptionLabel={(o) => o.name}
                value={REPORT_DEFS.find(r => r.id === selectedReportId) ?? REPORT_DEFS[0]}
                onChange={(_, v) => { if (v) setSelectedReportId(v.id); }}
                disableClearable
                sx={{ width: 240 }}
                slotProps={{ popper: { sx: { zIndex: 1400 } } }}
                ListboxProps={{ sx: { maxHeight: 260, p: 0 } }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select report…"
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: 12, bgcolor: '#fff' } }} />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id} style={{ padding: '7px 12px', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>{option.name}</div>
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{option.description}</div>
                  </li>
                )}
              />

              {/* Generate button */}
              <Button size="small" variant="contained"
                endIcon={<ArrowDropDownIcon sx={{ fontSize: '16px !important', ml: -0.5 }} />}
                onClick={(e) => setGenerateAnchor(e.currentTarget)}
                sx={{ fontSize: 11, fontWeight: 700, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' }, whiteSpace: 'nowrap', textTransform: 'none', px: 1.5, py: '3px', minWidth: 0, flexShrink: 0 }}>
                Generate
              </Button>
              <Menu anchorEl={generateAnchor} open={Boolean(generateAnchor)} onClose={() => setGenerateAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { minWidth: 190, mt: 0.5 } }}>
                <MenuItem dense sx={{ fontSize: 12, gap: 1, py: 0.8 }} onClick={() => { downloadReportExcel(); setGenerateAnchor(null); }}>
                  <XlsIcon sx={{ fontSize: 15, color: '#15803d' }} /> Download Excel (.xls)
                </MenuItem>
                <MenuItem dense sx={{ fontSize: 12, gap: 1, py: 0.8 }} onClick={() => { downloadReportPdf(); setGenerateAnchor(null); }}>
                  <PrintIcon sx={{ fontSize: 15, color: '#dc2626' }} /> Download / Print PDF
                </MenuItem>
              </Menu>
            </Box>

            {orders.length === 0 ? (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, color: 'text.secondary' }}>
                <ReportIcon sx={{ fontSize: 36, opacity: 0.25 }} />
                <Typography variant="body2">No orders found to generate reports.</Typography>
              </Box>
            ) : (
              /* Table fills all remaining height; thead + tfoot are sticky */
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {/* ── Order Status Summary ── */}
                {selectedReportId === 'order-status' && (() => {
                  const totalRev = orderStatusStats.reduce((a,s)=>a+s.revenue,0);
                  const totalCnt = orderStatusStats.reduce((a,s)=>a+s.count,0);
                  return (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6', position: 'sticky', top: 0, zIndex: 1 }}>
                          {(['Status','Orders','Revenue (₹)','Revenue Share %'] as const).map((h,i) => (
                            <th key={h} style={{ padding: '7px 12px', textAlign: i===0?'left':'right', borderBottom: '2px solid #e5e7eb', fontWeight: 700, color: '#374151', fontSize: 10, letterSpacing: 0.5, whiteSpace: 'nowrap', background: '#f3f4f6' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {orderStatusStats.map((s, idx) => (
                          <tr key={s.status} style={{ background: idx%2===0?'#fff':'#fafafa', borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '5px 12px' }}>
                              <span style={{ padding: '2px 7px', borderRadius: 4, background: statusBg(s.status as OrderStatus), color: '#fff', fontSize: 10, fontWeight: 700 }}>{s.label.toUpperCase()}</span>
                            </td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#1a56db' }}>{s.count}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatAmount(s.revenue)}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right' }}>
                              <span style={{ padding: '2px 7px', borderRadius: 4, background: '#f0fdf4', color: '#16a34a', fontWeight: 700 }}>{totalRev>0?((s.revenue/totalRev)*100).toFixed(1):0}%</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f3f4f6', borderTop: '2px solid #e5e7eb', position: 'sticky', bottom: 0 }}>
                          <td style={{ padding: '5px 12px', fontWeight: 700, fontSize: 11, color: '#374151' }}>Total</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#1a56db' }}>{totalCnt}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatAmount(totalRev)}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>100%</td>
                        </tr>
                      </tfoot>
                    </table>
                  );
                })()}

                {/* ── Party-wise Summary ── */}
                {selectedReportId === 'party-summary' && (() => {
                  const totalRev = partyStats.reduce((a,p)=>a+p.revenue,0);
                  const totalCnt = partyStats.reduce((a,p)=>a+p.count,0);
                  return (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6', position: 'sticky', top: 0, zIndex: 1 }}>
                          {(['Party / Retailer','Orders','Revenue (₹)','Last Order'] as const).map((h,i) => (
                            <th key={h} style={{ padding: '7px 12px', textAlign: i===0||i===3?'left':'right', borderBottom: '2px solid #e5e7eb', fontWeight: 700, color: '#374151', fontSize: 10, letterSpacing: 0.5, whiteSpace: 'nowrap', background: '#f3f4f6' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {partyStats.map((p, idx) => (
                          <tr key={p.partyId} style={{ background: idx%2===0?'#fff':'#fafafa', borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '5px 12px', fontWeight: 600, color: '#1a56db' }}>{p.name}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{p.count}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatAmount(p.revenue)}</td>
                            <td style={{ padding: '5px 12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{p.lastDate}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f3f4f6', borderTop: '2px solid #e5e7eb', position: 'sticky', bottom: 0 }}>
                          <td style={{ padding: '5px 12px', fontWeight: 700, fontSize: 11, color: '#374151' }}>Total — {partyStats.length} parties</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{totalCnt}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatAmount(totalRev)}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  );
                })()}

                {/* ── Daily Orders Report ── */}
                {selectedReportId === 'daily-orders' && (() => {
                  const totalCnt  = dailyStats.reduce((a,d)=>a+d.count,0);
                  const totalItems = dailyStats.reduce((a,d)=>a+d.items,0);
                  const totalRev  = dailyStats.reduce((a,d)=>a+d.revenue,0);
                  return (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6', position: 'sticky', top: 0, zIndex: 1 }}>
                          {(['Date','Orders','Total Items','Revenue (₹)'] as const).map((h,i) => (
                            <th key={h} style={{ padding: '7px 12px', textAlign: i===0?'left':'right', borderBottom: '2px solid #e5e7eb', fontWeight: 700, color: '#374151', fontSize: 10, letterSpacing: 0.5, whiteSpace: 'nowrap', background: '#f3f4f6' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {dailyStats.map((d, idx) => (
                          <tr key={d.date} style={{ background: idx%2===0?'#fff':'#fafafa', borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '5px 12px', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{d.date}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#1a56db' }}>{d.count}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', color: '#374151' }}>{d.items}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatAmount(d.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f3f4f6', borderTop: '2px solid #e5e7eb', position: 'sticky', bottom: 0 }}>
                          <td style={{ padding: '5px 12px', fontWeight: 700, fontSize: 11, color: '#374151' }}>Total — {dailyStats.length} days</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#1a56db' }}>{totalCnt}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#374151' }}>{totalItems}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatAmount(totalRev)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  );
                })()}

                {selectedReportId === 'rep-summary' && (() => {
                  const tot = { o: repStats.reduce((a,r)=>a+r.totalOrders,0), r: repStats.reduce((a,r)=>a+r.totalRevenue,0), p: repStats.reduce((a,r)=>a+r.pending,0), c: repStats.reduce((a,r)=>a+r.confirmed,0), d: repStats.reduce((a,r)=>a+r.delivered,0), x: repStats.reduce((a,r)=>a+r.cancelled,0) };
                  return (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6', position: 'sticky', top: 0, zIndex: 1 }}>
                          {(['Sales Rep','Orders','Revenue (₹)','Pending','Confirmed','Completed','Cancelled / Rejected'] as const).map((h, i) => (
                            <th key={h} style={{ padding: '7px 12px', textAlign: i===0?'left':'right', borderBottom: '2px solid #e5e7eb', fontWeight: 700, color: '#374151', fontSize: 10, letterSpacing: 0.5, whiteSpace: 'nowrap', background: '#f3f4f6' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {repStats.map((s, idx) => (
                          <tr key={s.repId} style={{ background: idx%2===0?'#fff':'#fafafa', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                            onClick={() => { setRepReportView(false); setSelectedRepIds([s.repId]); }}>
                            <td style={{ padding: '5px 12px', whiteSpace: 'nowrap' }}>
                              <span style={{ padding: '2px 7px', borderRadius: 4, background: '#ede9fe', color: '#7c3aed', fontSize: 11, fontWeight: 700 }}>👤 {s.repName}</span>
                            </td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#1a56db' }}>{s.totalOrders}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatAmount(s.totalRevenue)}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right' }}>{s.pending>0?<span style={{padding:'2px 7px',borderRadius:4,background:'#fff7ed',color:'#f97316',fontWeight:700}}>{s.pending}</span>:<span style={{color:'#d1d5db'}}>—</span>}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right' }}>{s.confirmed>0?<span style={{padding:'2px 7px',borderRadius:4,background:'#eff6ff',color:'#3b82f6',fontWeight:700}}>{s.confirmed}</span>:<span style={{color:'#d1d5db'}}>—</span>}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right' }}>{s.delivered>0?<span style={{padding:'2px 7px',borderRadius:4,background:'#f0fdf4',color:'#16a34a',fontWeight:700}}>{s.delivered}</span>:<span style={{color:'#d1d5db'}}>—</span>}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right' }}>{s.cancelled>0?<span style={{padding:'2px 7px',borderRadius:4,background:'#fef2f2',color:'#ef4444',fontWeight:700}}>{s.cancelled}</span>:<span style={{color:'#d1d5db'}}>—</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f3f4f6', borderTop: '2px solid #e5e7eb', position: 'sticky', bottom: 0 }}>
                          <td style={{ padding: '5px 12px', fontWeight: 700, fontSize: 11, color: '#374151' }}>Total</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#1a56db' }}>{tot.o}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatAmount(tot.r)}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#f97316' }}>{tot.p||'—'}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#3b82f6' }}>{tot.c||'—'}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>{tot.d||'—'}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#ef4444' }}>{tot.x||'—'}</td>
                        </tr>
                      </tfoot>
                    </table>
                  );
                })()}

                {selectedReportId === 'revenue-analysis' && (() => {
                  const totalRev = repStats.reduce((a,r)=>a+r.totalRevenue,0);
                  return (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6', position: 'sticky', top: 0, zIndex: 1 }}>
                          {(['Sales Rep','Orders','Revenue (₹)','Avg Order Value (₹)','Revenue Share %'] as const).map((h, i) => (
                            <th key={h} style={{ padding: '7px 12px', textAlign: i===0?'left':'right', borderBottom: '2px solid #e5e7eb', fontWeight: 700, color: '#374151', fontSize: 10, letterSpacing: 0.5, whiteSpace: 'nowrap', background: '#f3f4f6' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {repStats.map((s, idx) => {
                          const avg = s.totalOrders > 0 ? s.totalRevenue / s.totalOrders : 0;
                          const share = totalRev > 0 ? (s.totalRevenue / totalRev) * 100 : 0;
                          return (
                            <tr key={s.repId} style={{ background: idx%2===0?'#fff':'#fafafa', borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '5px 12px', whiteSpace: 'nowrap' }}>
                                <span style={{ padding: '2px 7px', borderRadius: 4, background: '#ede9fe', color: '#7c3aed', fontSize: 11, fontWeight: 700 }}>👤 {s.repName}</span>
                              </td>
                              <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#1a56db' }}>{s.totalOrders}</td>
                              <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatAmount(s.totalRevenue)}</td>
                              <td style={{ padding: '5px 12px', textAlign: 'right', color: '#374151' }}>{formatAmount(avg)}</td>
                              <td style={{ padding: '5px 12px', textAlign: 'right' }}>
                                <span style={{ padding: '2px 7px', borderRadius: 4, background: '#f0fdf4', color: '#16a34a', fontWeight: 700 }}>{share.toFixed(1)}%</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f3f4f6', borderTop: '2px solid #e5e7eb', position: 'sticky', bottom: 0 }}>
                          <td style={{ padding: '5px 12px', fontWeight: 700, fontSize: 11 }}>Total</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#1a56db' }}>{repStats.reduce((a,r)=>a+r.totalOrders,0)}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatAmount(totalRev)}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', color: '#374151' }}>{formatAmount(repStats.length>0?totalRev/repStats.reduce((a,r)=>a+r.totalOrders,0):0)}</td>
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>100%</td>
                        </tr>
                      </tfoot>
                    </table>
                  );
                })()}

                {selectedReportId === 'order-details' && (() => {
                  const repOrders = orders.filter(o=>o.salesRepTenantId!=null).sort((a,b)=>(a.salesRepName??'').localeCompare(b.salesRepName??''));
                  return (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6', position: 'sticky', top: 0, zIndex: 1 }}>
                          {(['Sales Rep','Order #','Party','Date','Amount (₹)','Status'] as const).map((h, i) => (
                            <th key={h} style={{ padding: '7px 12px', textAlign: i>=4?'right':'left', borderBottom: '2px solid #e5e7eb', fontWeight: 700, color: '#374151', fontSize: 10, letterSpacing: 0.5, whiteSpace: 'nowrap', background: '#f3f4f6' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {repOrders.map((o, idx) => (
                          <tr key={o.id} style={{ background: idx%2===0?'#fff':'#fafafa', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                            onClick={() => { setRepReportView(false); if (o.salesRepTenantId) setSelectedRepIds([o.salesRepTenantId]); }}>
                            <td style={{ padding: '5px 12px', whiteSpace: 'nowrap' }}>
                              <span style={{ padding: '2px 7px', borderRadius: 4, background: '#ede9fe', color: '#7c3aed', fontSize: 11, fontWeight: 700 }}>👤 {o.salesRepName??`Rep #${o.salesRepTenantId}`}</span>
                            </td>
                            <td style={{ padding: '5px 12px', color: '#1a56db', fontWeight: 600, whiteSpace: 'nowrap' }}>#{o.orderNumber??o.id}</td>
                            <td style={{ padding: '5px 12px', color: '#374151' }}>{retailers.get(o.retailerTenantId)?.name??`Party #${o.retailerTenantId}`}</td>
                            <td style={{ padding: '5px 12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{new Date(o.orderDate??o.createdAt).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'2-digit'})}</td>
                            <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatAmount(Number(o.totalAmount))}</td>
                            <td style={{ padding: '5px 12px' }}>
                              <span style={{ padding: '2px 7px', borderRadius: 4, background: statusBg(o.status), color: '#fff', fontSize: 10, fontWeight: 700 }}>{statusLabel(o.status).toUpperCase()}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f3f4f6', borderTop: '2px solid #e5e7eb', position: 'sticky', bottom: 0 }}>
                          <td colSpan={3} style={{ padding: '5px 12px', fontWeight: 700, fontSize: 11, color: '#374151' }}>Total — {repOrders.length} orders</td>
                          <td />
                          <td style={{ padding: '5px 12px', textAlign: 'right', fontWeight: 700, color: '#152346' }}>{formatAmount(repOrders.reduce((a,o)=>a+Number(o.totalAmount),0))}</td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  );
                })()}
              </Box>
            )}
          </Box>
        )}

        {!loading && !repReportView && filteredOrders.length === 0 && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 1, color: 'text.secondary' }}>
            <Typography variant="body2">No orders found for the selected filters.</Typography>
            <Button size="small" onClick={clearAll}>Clear Filters</Button>
          </Box>
        )}

        {!loading && !repReportView && filteredOrders.length > 0 && (
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Box sx={{ minWidth: 520 }}>
            {grouped.map(([groupKey, dayOrders]) => {
              const sample = dayOrders[0];
              const dateLabel = formatDateGroupHeader(sample.orderDate ?? sample.createdAt);
              const dayItems = dayOrders.reduce((s, o) => s + o.items.length, 0);
              const dayTotal = dayOrders.reduce((s, o) => s + (Number(o.totalAmount) || 0), 0);

              return (
                <Box key={groupKey}>
                  {/* Date group header */}
                  {(() => {
                    const allDaySelected = dayOrders.every(o => selectedIds.has(o.id));
                    const someDaySelected = dayOrders.some(o => selectedIds.has(o.id));
                    return (
                      <Box sx={{ display: 'grid', gridTemplateColumns: COLS, bgcolor: '#f3f4f6', borderBottom: '1px solid #e5e7eb', borderTop: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 1, minHeight: 38, alignItems: 'center' }}>
                        <Box />
                        <Box sx={{ gridColumn: '2 / 10', px: 1.5, py: 0.6, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#152346', whiteSpace: 'nowrap' }}>{dateLabel}</Typography>
                          <Typography sx={{ fontSize: 12, color: '#000', whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {'₹'}<Box component="span" sx={{ fontWeight: 700, color: '#152346' }}>{formatAmount(dayTotal)}</Box>{' · '}
                            <Box component="span" sx={{ fontWeight: 700, color: '#374151' }}>{dayOrders.length}</Box>{` order${dayOrders.length !== 1 ? 's' : ''} · `}
                            <Box component="span" sx={{ fontWeight: 700, color: '#374151' }}>{dayItems}</Box>{` item${dayItems !== 1 ? 's' : ''}`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Checkbox
                            size="small"
                            sx={{ p: 0.25 }}
                            checked={allDaySelected}
                            indeterminate={someDaySelected && !allDaySelected}
                            onChange={() => setSelectedIds(prev => {
                              const next = new Set(prev);
                              if (allDaySelected) {
                                dayOrders.forEach(o => next.delete(o.id));
                              } else {
                                dayOrders.forEach(o => next.add(o.id));
                              }
                              return next;
                            })}
                          />
                        </Box>
                      </Box>
                    );
                  })()}

                  {/* Order rows — single line each, expandable via the chevron */}
                  {dayOrders.map((order) => {
                    const peer = retailers.get(order.retailerTenantId);
                    const retailerName = peer?.name ?? `Retailer #${order.retailerTenantId}`;
                    const timeStr = formatTime(order.orderDate ?? order.createdAt);
                    const hasComment = (order.status === 'REJECTED' || order.status === 'CANCELLED') && !!order.cancelReason;
                    const repName = order.salesRepName
                      ?? (order.salesRepTenantId ? repsMap.get(order.salesRepTenantId) : undefined)
                      ?? (order.salesRepUserId ? `Rep #${order.salesRepUserId}` : undefined);
                    const isWholesaler = peer?.tenantType === 'WHOLESALER';
                    const badgeLetter = isWholesaler ? 'W' : 'R';
                    const badgeColors = isWholesaler ? { bg: '#7F77DD', fg: '#26215C' } : { bg: '#C17BAF', fg: '#FFFFFF' };
                    const expanded = expandedIds.has(order.id);
                    const shortage = shortageStore.get(order.id);
                    const hasShortage = (shortage?.shortages.length ?? 0) > 0;

                    return (
                      <Box key={order.id}>
                        <Box onClick={() => handleViewAndNavigate(order)}
                          sx={{ display: 'grid', gridTemplateColumns: COLS, alignItems: 'center', borderBottom: expanded ? 'none' : '1px solid #e5e7eb', bgcolor: '#fff', cursor: 'pointer', '&:hover': { bgcolor: '#f0f4ff' }, minHeight: 36 }}>

                          {/* Col 1: expand chevron */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={(e) => { e.stopPropagation(); toggleExpanded(order.id); }}>
                            <ArrowDropDownIcon sx={{ fontSize: 20, color: '#9ca3af', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                          </Box>

                          {/* Col 2: badge + buyer name + rep tag / due amount on second line */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, py: 0.25 }}
                            onClick={(e) => e.stopPropagation()}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                              <Box sx={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, bgcolor: badgeColors.bg, color: badgeColors.fg }}>
                                {badgeLetter}
                              </Box>
                              <Typography
                                sx={{ fontSize: 12, fontWeight: 600, color: '#1a56db', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25, cursor: peer ? 'pointer' : 'default', '&:hover': peer ? { textDecoration: 'underline' } : {} }}
                                onClick={() => peer && setViewParty(peer)}
                              >
                                {retailerName}
                              </Typography>
                              {repName && (
                                <Tooltip title={repName}>
                                  <Box component="span" sx={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.15, borderRadius: '5px', bgcolor: '#ede9fe', border: '1px solid #c4b5fd', color: '#7c3aed', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'default' }}>
                                    👤 Rep
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                            {peer?.linkedTenantParty?.outstanding != null && (
                              <Typography
                                sx={{ fontSize: 11, fontWeight: 700, color: peer.linkedTenantParty.outstanding > 0 ? '#dc2626' : '#16a34a', cursor: 'pointer', '&:hover': { textDecoration: 'underline' }, whiteSpace: 'nowrap', mt: '2px', ml: '26px' }}
                                onClick={() => setViewOutstandingParty(peer)}
                              >
                                Due: ₹{peer.linkedTenantParty.outstanding.toLocaleString()}
                              </Typography>
                            )}
                          </Box>

                          {/* Col 3: phone — wraps to a second line if it doesn't fit the column width */}
                          <Typography sx={{ fontSize: 11, color: '#000', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 }}>
                            {peer?.phone ? `📞 ${peer.phone}` : ''}
                          </Typography>

                          {/* Col 4: location — wraps to a second line if it doesn't fit the column width */}
                          <Typography sx={{ fontSize: 11, color: '#000', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 }}>
                            {[peer?.address, peer?.city].filter(Boolean).join(', ')}
                          </Typography>

                          {/* Col 5: order # + value — value always sits on its own line below the order id; shortage icon on a third line */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '2px' }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1a56db', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 }}>
                              #{order.orderNumber || order.id}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#152346', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.25 }}>
                                ₹{formatAmount(Number(order.totalAmount))}
                              </Typography>
                              {hasShortage && (
                                <Tooltip arrow placement="right" title={
                                  <Box>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>⚠ Shortages</Typography>
                                    {shortage!.shortages.map(s => (
                                      <Typography key={s.sku} sx={{ fontSize: 10 }}>{s.productName}: ordered {s.orderedQty}, received {s.receivedQty} (short <strong>{s.shortageQty}</strong>)</Typography>
                                    ))}
                                  </Box>
                                }>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'default' }}>
                                    <WarningIcon sx={{ fontSize: 13, color: '#f59e0b', flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#d97706', whiteSpace: 'nowrap' }}>Shortage</Typography>
                                  </Box>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>

                          {/* Col 6: invoice-uploaded tick, or spacer */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {order.invoiceFileName && (
                              <Tooltip title="Invoice Uploaded" arrow>
                                <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                              </Tooltip>
                            )}
                          </Box>

                          {/* Col 8: VIEW button — shared ViewButton component, same size/style everywhere it's used */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ViewButton onClick={() => handleViewAndNavigate(order)} />
                          </Box>

                          {/* Col 9: status badge + comment indicator — every status tag is the same total
                              width (78px); when the rejected/cancelled comment icon is shown, the badge
                              itself shrinks so badge + icon + gap still add up to that same 78px. */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4, width: 78 }}
                            onClick={(e) => e.stopPropagation()}>
                            <Box sx={{ px: 0.9, py: 0.25, borderRadius: 0.5, bgcolor: statusBg(order.status), color: '#fff', fontSize: 9, fontWeight: 800, letterSpacing: 0.4, whiteSpace: 'nowrap', textAlign: 'center', width: hasComment ? 61 : 78 }}>
                              {statusLabel(order.status).toUpperCase()}
                            </Box>
                            {hasComment && (
                              <Tooltip title={order.cancelReason ?? ''} arrow placement="top">
                                <CommentIcon sx={{ fontSize: 14, color: '#ef4444', cursor: 'default', flexShrink: 0 }} />
                              </Tooltip>
                            )}
                          </Box>

                          {/* Col 10: three-dot menu */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={(e) => e.stopPropagation()}>
                            <OrderRowMenu
                              order={order}
                              retailerName={retailerName}
                              actionLoading={actionLoading}
                              onAccept={() => handleAccept(order)}
                              onReject={(reason) => handleReject(order, reason)}
                              onComplete={() => handleComplete(order)}
                              onUploadInvoice={(file) => handleUploadInvoice(order, file)}
                            />
                          </Box>

                          {/* Col 11: checkbox */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={(e) => e.stopPropagation()}>
                            <Checkbox size="small"
                              checked={selectedIds.has(order.id)}
                              onChange={() => setSelectedIds((prev) => { const next = new Set(prev); if (next.has(order.id)) next.delete(order.id); else next.add(order.id); return next; })}
                              sx={{ p: 0.25 }} />
                          </Box>
                        </Box>

                        {/* Expanded detail — buyer details first, then order details */}
                        {expanded && (
                          <Box sx={{ px: 2, py: 1, bgcolor: '#fafbfc', borderBottom: '1px solid #e5e7eb' }}
                            onClick={(e) => e.stopPropagation()}>
                            <Typography sx={{ fontSize: 11, color: '#374151' }}>
                              <Box component="span" sx={{ fontWeight: 700, color: '#152346' }}>{retailerName}</Box>
                              {peer?.phone && <> · {peer.phone}</>}
                              {(peer?.address || peer?.city) && <> · {[peer.address, peer.city].filter(Boolean).join(', ')}</>}
                              {peer?.gstin && <> · GST: {peer.gstin}</>}
                              {peer?.drugLicenseNumber && <> · DL: {peer.drugLicenseNumber}</>}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4 }}>
                              <Typography sx={{ fontSize: 11, color: '#000' }}>
                                Order #{order.orderNumber || order.id} · {order.items?.length ?? 0} item{(order.items?.length ?? 0) !== 1 ? 's' : ''} · placed {timeStr}
                              </Typography>
                              {repName && (
                                <Box component="span" sx={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 0.3, px: 0.75, py: 0.15, borderRadius: '5px', bgcolor: '#ede9fe', border: '1px solid #c4b5fd', color: '#7c3aed', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                  👤 Via Rep: {repName}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
            </Box>

            {/* ── Pagination footer — part of the scrollable content, not pinned to the viewport ── */}
            <PaginationFooter
              page={pageNum}
              totalPages={Math.max(1, Math.ceil(filteredOrders.length / rowsPerPage))}
              total={filteredOrders.length}
              rowsPerPage={rowsPerPage}
              onPageChange={setPageNum}
              onRowsPerPageChange={v => setRowsPerPage(v as 50 | 100)}
              label="orders"
            />
          </Box>
        )}
      </Box>

      {/* ── Mobile filter Drawer ── */}
      <Drawer anchor="left" open={sidebarOpen} onClose={() => setSidebarOpen(false)}
        sx={{ display: { xs: 'block', md: 'none' } }}
        PaperProps={{ sx: { width: 220, fontSize: 13 } }}>
        <Box sx={{ px: 1.75, py: 1, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#f3f4f6' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151', letterSpacing: 0.6 }}>Filters</Typography>
          <Box component="button" onClick={() => { clearAll(); setSidebarOpen(false); }}
            sx={{ border: '1px solid #ef4444', bgcolor: 'transparent', color: '#ef4444', fontSize: 10, fontWeight: 700, px: 0.75, py: 0.2, borderRadius: 0.5, cursor: 'pointer', '&:hover': { bgcolor: '#fef2f2' } }}>
            × CLEAR
          </Box>
        </Box>
        <SidebarSection title="Network Type" onClear={selectedPartyTypes.length > 0 ? () => setSelectedPartyTypes([]) : undefined}>
          {([
            { value: 'RETAILER',    label: 'Retailer Orders' },
            { value: 'DISTRIBUTOR', label: 'Supplier Orders' },
            { value: 'SALESREP',    label: 'Sales Rep Orders' },
          ] as { value: PartyTypeFilter; label: string }[]).map(({ value, label }) => (
            <FormControlLabel key={value}
              control={<Checkbox size="small" checked={selectedPartyTypes.includes(value)} onChange={() => togglePartyType(value)} sx={{ p: 0.3 }} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <Typography sx={{ fontSize: 12, color: '#374151' }}>{label}</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: partyTypeColor(value) }}>({partyTypeCounts.get(value) ?? 0})</Typography>
                </Box>
              }
              sx={{ mx: 0, my: 0.1, display: 'flex', gap: 0.25, width: '100%' }} />
          ))}
        </SidebarSection>
        <SidebarSection title="Status" onClear={selectedStatuses.length > 0 ? () => setSelectedStatuses([]) : undefined}>
          {FILTER_STATUS_OPTIONS.map((opt) => {
            return (
              <FormControlLabel key={opt.value}
                control={<Checkbox size="small" checked={selectedStatuses.includes(opt.value)} onChange={() => toggleStatus(opt.value)} sx={{ p: 0.3 }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <Typography sx={{ fontSize: 12, color: '#374151' }}>{opt.label} Orders</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: statusBg(opt.value) }}>({statusCounts.get(opt.value) ?? 0})</Typography>
                  </Box>
                }
                sx={{ mx: 0, my: 0.1, display: 'flex', gap: 0.25, width: '100%' }} />
            );
          })}
        </SidebarSection>
        <SidebarSection title="Time Period" onClear={dateFilter !== 'ALL' ? () => setDateFilter('ALL') : undefined}>
          <RadioGroup value={dateFilter} onChange={(e) => setDateFilter(e.target.value as DateFilter)}>
            {(['ALL','TODAY','YESTERDAY','THIS_WEEK','THIS_MONTH'] as DateFilter[]).map((v) => (
              <FormControlLabel key={v} value={v}
                control={<Radio size="small" sx={{ p: 0.3 }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <Typography sx={{ fontSize: 12, color: '#374151' }}>{v === 'ALL' ? 'All Time' : v === 'TODAY' ? 'Current Day' : v === 'THIS_WEEK' ? 'Current Week' : v === 'THIS_MONTH' ? 'Current Month' : v.charAt(0) + v.slice(1).toLowerCase()}</Typography>
                    {/* Count hidden for "All Time" — keeping the code, just not rendering it.
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: DATE_FILTER_COLOR }}>({dateFilterCounts.get(v) ?? 0})</Typography>
                    */}
                    {v !== 'ALL' && (
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: DATE_FILTER_COLOR }}>({dateFilterCounts.get(v) ?? 0})</Typography>
                    )}
                  </Box>
                }
                sx={{ mx: 0, my: 0.1, width: '100%' }} />
            ))}
          </RadioGroup>
        </SidebarSection>
      </Drawer>

      {/* ── Subscription due reminder dialog ── */}
      <Dialog open={subDueOpen} onClose={() => setSubDueOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#b45309' }}>⚠️ Payment Due</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, mb: 1.5 }}>
            Your subscription payment is due. Please clear the balance to avoid service interruption.
          </Typography>
          {subGate.dueAmount > 0 && (
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#b45309', mb: 1 }}>
              Due: ₹{subGate.dueAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          )}
          <Typography sx={{ fontSize: 12, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 1, px: 1.5, py: 1, color: '#92400e' }}>
            Please contact <strong>Ardinotech Ltd</strong> to settle your outstanding invoice.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button size="small" onClick={() => {
            const action = pendingActionRef.current;
            pendingActionRef.current = null;
            setSubDueOpen(false);
            action?.();
          }}>Continue</Button>
          <Button size="small" variant="contained"
            sx={{ bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' } }}
            onClick={() => { pendingActionRef.current = null; setSubDueOpen(false); navigate('/my-subscription'); }}>
            Pay Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Party details dialog */}
      {viewParty && (
        <Dialog open onClose={() => setViewParty(null)} maxWidth="sm" fullWidth onClick={e => e.stopPropagation()}>
          <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1, pr: 10, display: 'flex', alignItems: 'center', gap: 1 }}>
            {viewParty.name}
            {viewParty.tenantType && (
              <Box component="span" sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, whiteSpace: 'nowrap', flexShrink: 0, color: (TENANT_TYPE_PALETTE[viewParty.tenantType] ?? TENANT_TYPE_PALETTE.OTHERS).bg }}>
                {viewParty.tenantType}
              </Box>
            )}
            <Box sx={{ flex: 1 }} />
            <IconButton size="small" onClick={() => setViewParty(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
              ✕
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ pt: 2 }}>
            {viewParty.linkedTenantParty?.outstanding != null && (
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 0.6, mb: 0.5,
                  borderBottom: '1px solid #f3f4f6', cursor: 'pointer', '&:hover': { bgcolor: '#fef2f2' },
                }}
                onClick={() => setViewOutstandingParty(viewParty)}
              >
                <Typography sx={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Outstanding</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: (viewParty.linkedTenantParty.outstanding ?? 0) > 0 ? '#dc2626' : '#16a34a', textDecoration: 'underline' }}>
                  ₹{(viewParty.linkedTenantParty.outstanding ?? 0).toLocaleString()}
                </Typography>
              </Box>
            )}
            {[
              { label: 'Type', value: viewParty.tenantType },
              { label: 'Industry', value: viewParty.industry },
              { label: 'Phone', value: viewParty.phone },
              { label: 'Email', value: viewParty.email },
              { label: 'Area', value: viewParty.area },
              { label: 'City', value: viewParty.city },
              { label: 'District', value: viewParty.district },
              { label: 'State', value: viewParty.state },
              { label: 'Pincode', value: viewParty.pincode },
              { label: 'Address', value: viewParty.address },
              { label: 'GST No', value: viewParty.gstin },
              { label: 'Drug License', value: viewParty.drugLicenseNumber },
              { label: 'Food License', value: viewParty.foodLicenseNumber },
            ].filter(r => r.value).map(r => (
              <Box key={r.label} sx={{ display: 'flex', gap: 2, py: 0.6, borderBottom: '1px solid #f3f4f6' }}>
                <Typography sx={{ fontSize: 12, color: '#6b7280', width: 110, flexShrink: 0, fontWeight: 600 }}>{r.label}</Typography>
                <Typography sx={{ fontSize: 13, color: '#111827', wordBreak: 'break-all' }}>{r.value}</Typography>
              </Box>
            ))}
          </DialogContent>
          <DialogActions sx={{ px: 2.5, py: 1.5 }}>
            <Button size="small" onClick={() => setViewParty(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      <OutstandingInvoicesDialog
        tenantPartyId={viewOutstandingParty?.linkedTenantParty?.id ?? null}
        partyName={viewOutstandingParty?.name}
        onClose={() => setViewOutstandingParty(null)}
      />

      <Snackbar
        open={Boolean(actionSuccess)}
        autoHideDuration={3000}
        onClose={() => setActionSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setActionSuccess('')} severity="success" sx={{ width: '100%' }}>
          {actionSuccess}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sidebar section helper
// ---------------------------------------------------------------------------

function SidebarSection({ title, onClear, children }: { title: string; onClear?: () => void; children: React.ReactNode }) {
  return (
    <Box sx={{ px: 1.75, py: 1, borderBottom: '1px solid #e5e7eb' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: 0.4 }}>{title}</Typography>
        {onClear && (
          <Box component="button" onClick={onClear}
            sx={{ border: 'none', bgcolor: 'transparent', color: '#9ca3af', fontSize: 10, fontWeight: 700, cursor: 'pointer', p: 0, '&:hover': { color: '#6b7280' } }}>
            CLEAR
          </Box>
        )}
      </Box>
      {children}
    </Box>
  );
}
